/**
 * Pricing breakdown helpers (v6.2).
 *
 * STRICT RULES — zero tolerance:
 *  1. vAuto advertised price is ALWAYS the public price source of truth.
 *  2. Final Price Before Taxes = vAuto price + doc fee + title fee. NOTHING else.
 *  3. Public/everyone rebates (Customer Cash, Bonus Cash, GMF Bonus Cash, Consumer
 *     Cash, Cash Allowance) may be itemized in the breakdown ONLY when they
 *     reconcile to MSRP − Dealer Discount − Public Rebates == vAuto Price.
 *  4. If they do NOT reconcile but vAuto's gap == Dealer Discount alone, then
 *     public rebates are "on top" — surfaced separately as Additional Public
 *     Savings (never subtracted from displayed price).
 *  5. Otherwise: mismatch. Show MSRP / Dealer Discount / vAuto Price only.
 *  6. Private/conditional offers (Military, First Responder, Educator, Healthcare,
 *     College, Employee, Supplier, Costco, Loyalty, Conquest, Trade Assistance,
 *     Lease Pull-Ahead, anything proof-required) NEVER appear in the price math.
 */

// Substring patterns that identify a private/conditional offer by name.
const PRIVATE_RE =
  /(military|first[\s-]?responder|educator|healthcare|college|employee|supplier|costco|loyalty|conquest|trade assistance|lease pull[\s-]?ahead)/i;

// Canonical incentive IDs known to be private (set by backend enrichment).
const PRIVATE_IDS = new Set([
  "gm_military",
  "gm_first_responder",
  "gm_educator",
  "gm_healthcare",
  "gm_college",
  "gm_employee",
  "gm_supplier",
  "costco",
  "gm_loyalty",
  "gm_conquest",
  "gm_trade_assistance",
  "lease_pull_ahead",
]);

// Substring patterns that identify a PUBLIC/everyone offer by name.
// Order matters: more specific names first. Used to filter offers that can
// safely appear in the price breakdown when reconciliation passes.
const PUBLIC_NAME_RE =
  /(chevrolet\s+gmf\s+bonus\s+cash|gmf\s+bonus\s+cash|chevrolet\s+bonus\s+cash|consumer\s+cash|cash\s+allowance|customer\s+cash|bonus\s+cash)/i;

const PUBLIC_IDS = new Set([
  "gmf_bonus_cash",
  "customer_cash",
  "bonus_cash",
]);

/**
 * Return true when the offer is safe to include in PUBLIC pricing math.
 * Defense in depth: four independent signals must all agree.
 */
export function isPublicOffer(o) {
  if (!o) return false;
  const id = String(o.incentive_id || "").toLowerCase();
  const name = String(o.name || "");
  if (PRIVATE_IDS.has(id)) return false;
  if (o.proof_required === true) return false;
  if (o.private_offer === true) return false;
  if (PRIVATE_RE.test(name)) return false;
  if (PUBLIC_IDS.has(id)) return true;
  if (PUBLIC_NAME_RE.test(name)) return true;
  return false;
}

/**
 * Return true when the offer is private/conditional (informational only).
 */
export function isPrivateOffer(o) {
  if (!o) return false;
  const id = String(o.incentive_id || "").toLowerCase();
  const name = String(o.name || "");
  if (PRIVATE_IDS.has(id)) return true;
  if (o.proof_required === true) return true;
  if (o.private_offer === true) return true;
  if (PRIVATE_RE.test(name)) return true;
  return false;
}

/**
 * Reduce a list of offers to a single integer total amount.
 */
function sumAmounts(offers) {
  return (offers || []).reduce((s, o) => s + (Number(o.amount) || 0), 0);
}

/**
 * Build the canonical pricing breakdown for a NEW vehicle.
 *
 * Inputs: a vehicle doc (from /api/vehicles or /api/vehicles/slug/{slug}).
 *
 * Output shape:
 *   {
 *     mode: "reconciled" | "bonus_on_top" | "mismatch" | "simple",
 *     msrp: number | null,
 *     dealerDiscount: number,      // amount displayed on the Dealer Discount row
 *     publicInlineOffers: Array<{name, amount}>,  // shown between Dealer Discount and Price
 *     additionalPublicOffers: Array<{name, amount}>, // shown BELOW price as "Additional Public Savings"
 *     vautoPrice: number,          // ALWAYS exactly v.price
 *     gap: number,                 // msrp - vautoPrice (>= 0)
 *     reconciled: boolean,
 *     mismatch: boolean,           // true ONLY for the "mismatch" mode
 *   }
 */
export function buildPriceStack(v) {
  const price = Number(v?.price) || 0;
  const msrp = v?.msrp && v.msrp > 0 ? Number(v.msrp) : null;
  const pdfDiscount = v?.dealer_discount && v.dealer_discount > 0 ? Number(v.dealer_discount) : 0;
  const isNew = v?.status === "new";
  const gap = msrp && msrp > price ? msrp - price : 0;

  // Used vehicles or no MSRP → simple stack (just price + fees).
  if (!isNew || !msrp || gap <= 0) {
    return {
      mode: "simple",
      msrp: null,
      dealerDiscount: 0,
      publicInlineOffers: [],
      additionalPublicOffers: [],
      vautoPrice: price,
      gap: 0,
      reconciled: true,
      mismatch: false,
    };
  }

  const allOffers = Array.isArray(v.byers_pdf_offers) ? v.byers_pdf_offers : [];
  const publicOffers = allOffers
    .filter(isPublicOffer)
    .map((o) => ({ name: o.name, amount: Number(o.amount) || 0 }))
    .filter((o) => o.amount > 0);

  // v6.5.5 — Public rebates (GMF Bonus Cash, Customer Cash, etc.) are now
  // ALWAYS surfaced as "Available Public Rebates" BELOW Scott's Price.
  // The previous CASE 1 (reconciled inline) rendering was visually implying
  // these rebates were already baked into Scott's Price, even though they
  // are OPTIONAL conditional offers (e.g. GMF Bonus Cash is finance-only and
  // requires GM Financial financing). Keeping them inline created the
  // "Chevrolet GMF Bonus Cash already in Scott's Price" classification bug.
  //
  // Dealer Discount displays as MSRP - vAuto Price (the true gap), with NO
  // public-rebate breakdown inline. The selectable Deal Desk is the only
  // place a public rebate ever reduces the customer's deal.
  const dealerDiscountCandidate = pdfDiscount > 0 ? pdfDiscount : gap;

  const TOL = 1; // $1 rounding tolerance

  // CASE A: gap reconciles via Dealer Discount alone (most vehicles).
  if (Math.abs(dealerDiscountCandidate - gap) <= TOL) {
    return {
      mode: "bonus_on_top",
      msrp,
      dealerDiscount: gap,
      publicInlineOffers: [],         // NEVER inline anymore — see note above
      additionalPublicOffers: publicOffers,
      vautoPrice: price,
      gap,
      reconciled: true,
      mismatch: false,
    };
  }

  // CASE B: Mismatch — Dealer Discount alone doesn't explain the gap. Render
  // the computed dealer discount (= gap) and expose public offers as
  // "Available Public Rebates" below the price. Flag mismatch for admin only.
  return {
    mode: "mismatch",
    msrp,
    dealerDiscount: gap,
    publicInlineOffers: [],
    additionalPublicOffers: publicOffers,
    vautoPrice: price,
    gap,
    reconciled: false,
    mismatch: publicOffers.length === 0 ? true : false, // suppress mismatch flag when public offers explain the difference
  };
}

/**
 * Filter byers_pdf_offers down to PRIVATE-only entries for the
 * "Other Offers You May Qualify For" informational section.
 */
export function privateOffersForVehicle(v) {
  const all = Array.isArray(v?.byers_pdf_offers) ? v.byers_pdf_offers : [];
  return all.filter(isPrivateOffer);
}

/**
 * Compute the total public-rebate savings shown in the breakdown.
 * Used for VehicleCard's compact "Public rebates available" badge.
 * v6.5.5 — Public rebates are always in additionalPublicOffers (never inline).
 */
export function publicSavingsBadgeAmount(stack) {
  if (!stack) return 0;
  return sumAmounts(stack.additionalPublicOffers);
}
